/**
 * -----------------------------------------------------------------------------
 * Groups Hierarchy View Page
 * -----------------------------------------------------------------------------
 * This page is used to display the groups of the organization in
 * a hierarchy view.
 */

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationGroupsQuery } from "../../../api/network/adminApiServices";
import { TListingQueryParams, setListingQueryParams } from "../../../api/store/commonSlice";
import { TLegacyGroup } from "../../../api/types/Group";
import { TListingColumn } from "../../../components/modalEditListingColumns";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { localStorageKeys, useLocalStorage } from "../../../utils/localStorageItems";
import GroupHierarchyHeaderView from "./components/groupHierarchyHeaderView";
import GroupHierarchyView from "./components/groupHierarchyView";
import { GHContextProvider, useGHContext } from "./contexts/groupHierarchy.context";
import AdminsGroupsCreateNew from "./createNewGroup";
import ModalConfirmMoveGroup from "./modalConfirmMoveGroup";


const all_columns = [
  "Name",
  "Description",
  "No. Of Active Assets",
  "No. Of Inactive Assets",
  "Parent Group",
]

const ScreenDashboardAdminGroupsHierarchy = () => {
  const listingQueryParams: TListingQueryParams = useSelector((state: any) => state.commonReducer.listingQueryParams);
  const { groups: orgGroupsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Groups);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns
      .map((colItem, _) => {
        return {
          name: colItem,
          show: !!savedColumns ? savedColumns.includes(colItem) : true
        }
      })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(setListingQueryParams({ ...listingQueryParams, groups: { ...orgGroupsQueryParams, page: 1, search: value } }));
  }, 500);

  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error
  } = useOrganizationGroupsQuery(orgGroupsQueryParams);

  const { count, next, previous, results } = dataOrgGroups || {};

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || [] as TLegacyGroup[]).map((item: TLegacyGroup, index: number) => (
      {
        navLink: `${routeUrls.dashboardChildren.adminChildren.groups}/${item.groupid}`,
        cellData: [
          item?.displayname || item?.groupid, // "Group Name",
          item?.description || '-', // "Description",
          item?.active_inactive_vehicle_counts?.active_vehicle_count, // "Active Vehicles", // TODO: Need to update this
          item?.active_inactive_vehicle_counts?.inactive_vehicle_count, // "Inactive Vehicles", // TODO: Need to update this
          !!item?.parentgroupid && item?.parentgroupid !== 'undefined' // "Parent Group"
            ? item?.parentgroupid
            : '-',
        ].filter((_, index) => columns[index].show)
      }))
    : [];

  // rendering
  return (
    <GHContextProvider>

      {/* Header */}
      <GroupHierarchyHeaderView />

      {/* Main Group Hierarchy Area */}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4 group-[]/hierarchy-modal:px-0`}>
        <GroupHierarchyView />
      </div>

      <AdminsGroupsCreateNew />
      <ModalConfirmMoveGroup />
    </GHContextProvider>
  )
}

export default ScreenDashboardAdminGroupsHierarchy;